import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Loader2 } from 'lucide-react';
import { whatsappNumber } from '../data/mockData';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const Contact = () => {
  const { language } = useLanguage();
  const t = language === 'fr';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Error');
      
      toast.success(t ? 'Message envoyé !' : 'Message sent!', {
        description: t ? 'Nous vous répondrons rapidement.' : 'We will respond shortly.',
      });
      
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t ? 'Erreur' : 'Error', {
        description: t ? 'Veuillez réessayer.' : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F2E8] dark:bg-[#0F1A14]">
      {/* Header */}
      <section className="bg-[#1A5C38] py-12 md:py-16">
        <div className="container text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Contact
          </h1>
          <p className="text-white/80 max-w-xl mx-auto">
            {t 
              ? 'Une question ? Un projet ? Contactez-nous.'
              : 'A question? A project? Contact us.'}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              
              {/* Contact Info */}
              <div className="md:col-span-2 space-y-6">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white dark:bg-[#1A2B22] rounded-2xl p-6"
                >
                  <h3 className="font-semibold text-[#1A5C38] dark:text-white mb-4">
                    {t ? 'Nos coordonnées' : 'Our details'}
                  </h3>
                  
                  <div className="space-y-4">
                    <a href={`tel:${whatsappNumber}`} className="flex items-center gap-3 text-gray-600 dark:text-gray-300 hover:text-[#1A5C38] dark:hover:text-[#F5A623] transition-colors">
                      <Phone size={18} className="text-[#1A5C38] dark:text-[#F5A623]" />
                      <span>{whatsappNumber}</span>
                    </a>
                    
                    <a href="mailto:contact@oceana-ci.com" className="flex items-center gap-3 text-gray-600 dark:text-gray-300 hover:text-[#1A5C38] dark:hover:text-[#F5A623] transition-colors">
                      <Mail size={18} className="text-[#1A5C38] dark:text-[#F5A623]" />
                      <span>contact@oceana-ci.com</span>
                    </a>
                    
                    <div className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
                      <MapPin size={18} className="text-[#1A5C38] dark:text-[#F5A623] flex-shrink-0 mt-0.5" />
                      <span>Abidjan, Côte d'Ivoire</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#1A5C38] rounded-2xl p-6 text-white"
                >
                  <h3 className="font-semibold mb-2">
                    {t ? 'Professionnel ?' : 'Professional?'}
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    {t 
                      ? 'Hôtels, restaurants, supermarchés — nous avons une offre adaptée.'
                      : 'Hotels, restaurants, supermarkets — we have a tailored offer.'}
                  </p>
                  <a 
                    href={`https://wa.me/${whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#F5A623] text-[#1A5C38] font-semibold px-4 py-2 rounded-full text-sm hover:bg-[#E09000] transition-colors"
                  >
                    WhatsApp
                  </a>
                </motion.div>
              </div>

              {/* Form */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="md:col-span-3"
              >
                <form onSubmit={handleSubmit} className="bg-white dark:bg-[#1A2B22] rounded-2xl p-6 md:p-8 shadow-sm">
                  <h3 className="font-semibold text-[#1A5C38] dark:text-white mb-6">
                    {t ? 'Envoyez-nous un message' : 'Send us a message'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder={t ? 'Nom *' : 'Name *'}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                      />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Email *"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                      />
                    </div>
                    
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder={t ? 'Téléphone' : 'Phone'}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                    />
                    
                    <textarea
                      name="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={t ? 'Votre message *' : 'Your message *'}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#0F1A14] dark:text-white focus:outline-none focus:border-[#1A5C38] resize-none"
                    />
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#1A5C38] hover:bg-[#145230] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                      {t ? 'Envoyer' : 'Send'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
